import React, { useEffect } from 'react'
import { GestureResponderEvent, TouchableOpacity } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { ScrollView } from 'react-native-gesture-handler'
import { KEY_ACCESS_SELECTED_CONTACT, useAuth, useNotify, useTheme } from '@/presentation/hooks'
import { ModalBase, Box, Button, Text, List, Loading, isSelectingContactState, localStorageState } from '@/presentation/components'
import { isLoadingState, contatoClientesSelectState, portalContatoState, isLoadingContatoClientesState } from '@/presentation/pages/Login/components'
import { styles } from './styles'
import { PortalContatoClientes, PortalContatoPermissoes } from '@/domain/usecases'
import { PortalContatoClienteModel, PortalContatoModel } from '@/domain/models'
import { useNavigation } from '@react-navigation/native'
import { NavigationRoute } from '@/main/config/router-navigation'

type ModalContatoSelectProps = {
  isOpen: boolean
  setOpen: (value: boolean) => void
  getPermissoes: PortalContatoPermissoes
  getContatoClientes: PortalContatoClientes
}

export const ModalContatoSelect: React.FC<ModalContatoSelectProps> = ({ isOpen, setOpen, getPermissoes, getContatoClientes }) => {
  const theme = useTheme()
  const notify = useNotify()
  const navigation = useNavigation<any>()
  const { setContato, setContatoPermissoes } = useAuth()
  const isLoading = useRecoilValue(isLoadingState)
  const [contatoClientes, setContatoClientesSelect] = useRecoilState(contatoClientesSelectState)
  const contato = useRecoilValue(portalContatoState)
  const setIsSelectingContact = useSetRecoilState(isSelectingContactState)
  const localStorageAdapter = useRecoilValue(localStorageState)
  const [loadingClientes, setLoadingClientes] = useRecoilState(isLoadingContatoClientesState)

  const localStorageContact = localStorageAdapter.get(KEY_ACCESS_SELECTED_CONTACT) as PortalContatoModel

  const handleError = (error: Error): void => {
    notify.show(error.message, { variant: 'error' })
  }

  const buscarContatoClientes = (codContato: number): void => {
    setLoadingClientes(true)
    getContatoClientes
      .get({
        codContato
      })
      .then((res) => {
        if (res.length === 1) {
          setOpen(false)
          notify.info('Só existe um cliente vinculado ao contato')
          return
        }
        setContatoClientesSelect(res)
      })
      .catch(handleError)
      .finally(() => {
        setLoadingClientes(false)
      })
  }

  const handleSelectContato = (event: GestureResponderEvent, contatoCliente: PortalContatoClienteModel): void => {
    event.preventDefault()

    const contatoFinal = contato.CodContato ? contato : localStorageContact

    if (contatoFinal.CodCliente === contatoCliente.CodCliente) {
      setOpen(false)
      setIsSelectingContact(false)

      return
    }

    getPermissoes
      .get({
        codCliente: contatoCliente.CodCliente,
        codContato: contatoFinal.CodContato
      })
      .then((res) => {
        localStorageAdapter.set(KEY_ACCESS_SELECTED_CONTACT, { ...contatoFinal, CodCliente: contatoCliente.CodCliente })
        setContato({ ...contatoFinal, CodCliente: contatoCliente.CodCliente })
        setContatoPermissoes(res)

        setOpen(false)
        setIsSelectingContact(false)

        navigation.navigate(NavigationRoute.Home)
      })
      .catch(handleError)
  }

  const handleCancel = (): void => {
    setOpen(false)
    setContatoClientesSelect([])
  }

  useEffect(() => {
    if (contatoClientes.length > 0 || !isOpen) return

    if (!contato?.CodContato && !localStorageContact?.CodContato) return

    buscarContatoClientes(contato.CodContato ?? localStorageContact.CodContato)
  }, [isOpen])

  return (
    <ModalBase visible={isOpen} onClose={handleCancel}>
      <Box style={styles.container}>
        {isLoading || loadingClientes
          ? <Loading text='Realizando login' />
          : <>
              <Text variant='subtitle1' style={{ fontWeight: '600' }}>Selecione o contato para Login</Text>
              <Box style={styles.scrollArea}>
                <ScrollView
                  style={styles.scrollView}
                  contentContainerStyle={styles.scrollContent}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                >
                  {contatoClientes?.map((client) => (
                    <TouchableOpacity
                      key={client.CodCliente}
                      style={styles.listItem}
                      onPress={(event) => handleSelectContato(event, client)}
                    >
                      <List.Item
                        title={client.NomeCliente}
                        description={`Código ${client.CodCliente.toString().padStart(6, '0')} - ${client.NomeCliente}`}
                        left={(props) => <List.Icon {...props} icon="account" />}
                        titleStyle={{ fontWeight: '600' }}
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </Box>

              <Box style={styles.buttonArea}>
                <Button
                  color={theme.colors.shadow}
                  variant="text"
                  title="Cancelar"
                  onPress={handleCancel}
                  disabled={isLoading}
                />
              </Box>
            </>
          }
      </Box>
    </ModalBase>
  )
}